import { useMemo } from 'react';

import { Label, DataTable, ErrorMessage, Loading, type TableColumn } from '@/src/ui';
import type { ExamSummaryDTO } from '../models/examSummaryDTOs';
import { ReportCellStyle, ReportHeaderStyle, Constants } from '@/src/utils';

interface ExamSummaryTableProps {
    data: ExamSummaryDTO[];
    isLoading: boolean;
    error: string;
}

export default function ExamSummaryTable({ data, isLoading, error }: ExamSummaryTableProps) {
    const { benchmark, others } = useMemo(() => {
        const benchmark: ExamSummaryDTO[] = [];
        const others: ExamSummaryDTO[] = [];
        data.forEach((item) => {
            if (item.examTypeId === Constants.EXAM_TYPE_MASTER_IDS.BENCHMARK) benchmark.push(item);
            else others.push(item);
        });
        return { benchmark, others };
    }, [data]);

    // Flatten bands and include color info
    const flattenBands = (rows: ExamSummaryDTO[]) =>
        rows.map((row) => ({
            ...row,
            ...Object.fromEntries(
                Object.entries(row.bands).map(([key, value]) => [
                    key,
                    value != null ? `${value}%` : '--',
                ])
            ),
        }));

    const benchmarkData = flattenBands(benchmark);
    const otherData = flattenBands(others);

    const buildColumns = (rows: typeof benchmarkData): TableColumn[] => {
        if (!rows.length) return [];
        const bandKeys = Object.keys(rows[0]).filter(
            (key) =>
                ![
                    'groupName',
                    'gradeShortName',
                    'studentsTested',
                    'averageScore',
                    'examTypeId',
                    'examName',
                    'bands',
                ].includes(key)
        );

        const bandColumns: TableColumn[] = bandKeys.map((key) => ({
            dataKey: key,
            header: key,
            allowSort: true,
            align: 'center',
            formatter: (row: any) => {
                const value = row[key];
                if (!value) return '--';
                return <Label color={Constants.bandColors[key]}>{value}</Label>;
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        }));

        return [
            {
                dataKey: 'groupName',
                header: 'Group',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'studentsTested',
                header: 'Students Tested',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'averageScore',
                header: 'Average Score',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: any) => {
                    const value = row.averageScore;
                    if (value == null) return '--';
                    return `${value}%`;
                },
            },
            ...bandColumns,
        ];
    };

    if (isLoading) return <Loading message="Loading Exam Summary Report..." />;
    if (error) return <ErrorMessage error={error} />;

    if (!benchmarkData.length && !otherData.length) {
        return (
            <div className="w-full text-center py-12 text-gray-600">
                No exam summary data available.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 w-full">
            {benchmarkData.length > 0 && (
                <div className="w-full overflow-auto">
                    <hr className="border-t border-gray-300 my-2" />
                    <br />

                    <div className="pl-4">
                        <h2
                            className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                            style={{
                                fontFamily: 'Verdana, Geneva, sans-serif',
                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            Benchmark
                        </h2>
                    </div>
                    <DataTable
                        columns={buildColumns(benchmarkData)}
                        data={benchmarkData}
                        tableName="Exam Summary - Benchmark"
                        rowsPerPageInitial={50}
                        enableExportCSV
                        alternatingRowColor
                    />
                </div>
            )}

            {otherData.length > 0 && (
                <div className="w-full overflow-auto">
                    <hr className="border-t border-gray-300 my-2" />
                    <br />

                    <div className="pl-4">
                        <h2
                            className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                            style={{
                                fontFamily: 'Verdana, Geneva, sans-serif',
                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            Unit Test & Diagnostic
                        </h2>
                    </div>
                    <DataTable
                        columns={buildColumns(otherData)}
                        data={otherData}
                        tableName="Exam Summary - Others"
                        rowsPerPageInitial={50}
                        enableExportCSV
                        alternatingRowColor
                        defaultSortColumn="groupName"
                        defaultSortDirection="asc"
                    />
                </div>
            )}
        </div>
    );
}
